import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";

import { getInsight, queryKeys, staleTimes } from "@/api";
import { useAudienceContext, useAudienceActions } from "@/context";
import { useToken } from "@/hooks/core";
import { useClient } from "@/hooks/useClient";
import { unwrap } from "@/utils";

/** Query options for an audience's insight, shared with the Compare page */
export const insightQuery = (
  getToken: () => Promise<string>,
  audienceId: string,
  client: string,
  segment = false
) => ({
  queryKey: queryKeys.insightDetail(audienceId, client, segment),
  queryFn: async () => unwrap(await getInsight(await getToken(), audienceId, false, client, segment)),
  staleTime: staleTimes.insight,
});

/**
 * The insight for an audience, cached per audience. Its audience is loaded
 * into the audience context if another audience is there, so breadcrumbs and
 * the audience pages show the right one.
 */
export const useInsight = (audienceId: string | undefined, segment: boolean = false) => {
  const { currentAudience } = useAudienceContext();
  const { load } = useAudienceActions();
  const getToken = useToken();
  const client = useClient();

  const { data, isLoading, error } = useQuery({
    ...insightQuery(getToken, audienceId ?? "", client, segment),
    enabled: !!audienceId,
  });

  const loaded = data?.audience;
  useEffect(() => {
    if (loaded && currentAudience?.audience_id !== loaded.audience_id) {
      load(loaded);
    }
  }, [currentAudience?.audience_id, load, loaded]);

  return {
    audience: data?.audience ?? null,
    insight: data?.insight ?? null,
    loading: isLoading,
    error: error?.message ?? null,
  };
};
